import type { MouseEvent } from 'react';
import { useAuthStore } from '@/store/auth';
import { canOpenDriverProfiles, isPlainLeftClick, sessionDriverDisplay, type DriverCellRow } from '@/lib/sessionDriver';
import styles from './DriverCell.module.css';

/** In-app navigation for a page that was not handed onNavigate: the same
 *  push App.tsx's navigate does, announced the way App listens (popstate). */
export function navigateInApp(path: string): void {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/**
 * The Driver column (item 9, 2026-10-23): who started the session, linked
 * to the driver profile when the Api says it opens for this viewer and the
 * viewer holds User:read (lib/sessionDriver.ts). The link stops the row's
 * own click (which opens the session), so a row still opens its session
 * everywhere but on the name. A modified click opens a new tab as links do.
 */
export function DriverCell({ row, onNavigate }: { row: DriverCellRow; onNavigate?: (path: string) => void }) {
  const canOpen = useAuthStore((s) => canOpenDriverProfiles(s.user));
  const shown = sessionDriverDisplay(row, canOpen);
  if (!shown.href) {
    return <span className={shown.muted ? styles.muted : styles.text}>{shown.text}</span>;
  }
  const href = shown.href;
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
    if (!isPlainLeftClick(e)) return;
    e.preventDefault();
    (onNavigate ?? navigateInApp)(href);
  };
  return (
    <a className={styles.link} href={href} onClick={onClick} title={`Open ${shown.text}'s driver profile`}>
      {shown.text}
    </a>
  );
}
